import { useEffect, useState } from 'react';
import { faThumbsDown as faThumbsDownOutline } from '@fortawesome/pro-regular-svg-icons/faThumbsDown';
import { faThumbsUp as faThumbsUpOutline } from '@fortawesome/pro-regular-svg-icons/faThumbsUp';
import { faThumbsDown } from '@fortawesome/pro-solid-svg-icons/faThumbsDown';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import {
  RECOMMENDATION_NONE,
  RECOMMENDATION_OTHERS,
  RECOMMENDATION_YES,
} from '../../models/ReviewFlowStore';
import { BackgroundLayout } from './components/BackgroundLayout';

const REVIEW_FAQ_URL =
  'https://support.urbansitter.com/hc/en-us/articles/4404385784219';

type ThumbsKey = 'respectsYourTime' | 'accurateJobDescription' | 'paysBasedOnBooking';

export const Main = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const flow = stores.reviewFlow;

  const [submitting, setSubmitting] = useState(false);
  const parentId = pathId ? Number(pathId) : undefined;

  useEffect(() => {
    document.title = t('pages.parentFeedback.main.pageTitle');
  }, [t]);

  useEffect(() => {
    if (!user?.sitter) {
      let fallback = '/';
      try {
        const ref = document.referrer;
        if (ref) {
          const url = new URL(ref, window.location.origin);
          if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
            fallback = `${url.pathname}${url.search}`;
          }
        }
      } catch {
        fallback = '/';
      }
      navigate(fallback, { replace: true });
      return;
    }
    if (!parentId || !user?.id) return;
    flow.loadParentAndReview(parentId, user.id);
  }, [user?.id, user?.sitter, parentId, flow, navigate]);

  const review = flow.review;
  const parent = flow.parent;
  const isPositive = review?.recommendation?.id === RECOMMENDATION_YES;

  const setRecommendation = (id: number): void => {
    if (id === RECOMMENDATION_YES) {
      flow.setReview({
        recommendation: { id },
        accurateJobDescription: true,
        paysBasedOnBooking: true,
        respectsYourTime: true,
      });
    } else {
      flow.setReview({
        recommendation: { id },
        accurateJobDescription: undefined,
        paysBasedOnBooking: undefined,
        respectsYourTime: undefined,
      });
    }
  };

  const canSubmit = ((): boolean => {
    if (!review?.recommendation?.id) return false;
    return (
      review.accurateJobDescription !== undefined &&
      review.paysBasedOnBooking !== undefined &&
      review.respectsYourTime !== undefined
    );
  })();

  const submit = async (): Promise<void> => {
    if (!canSubmit || !parentId || !review?.recommendation) return;
    setSubmitting(true);
    try {
      if (review.recommendation.id === RECOMMENDATION_YES) {
        await flow.saveReview();
        navigate(`/parent/${parentId}/feedback/success`);
      } else if (review.recommendation.id === RECOMMENDATION_NONE) {
        navigate(`/parent/${parentId}/feedback/safety`);
      } else if (review.recommendation.id === RECOMMENDATION_OTHERS) {
        navigate(`/parent/${parentId}/feedback/message`);
      }
    } catch {
      toast.error(t('pages.parentFeedback.main.submitFailed'));
      setSubmitting(false);
    }
  };

  if (flow.loading || !review || !parent) {
    return (
      <BackgroundLayout>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </BackgroundLayout>
    );
  }

  const name = parent.firstName ?? '';

  const recommendationOptions: Array<{
    id: number;
    labelKey: string;
    bodyKey: string;
  }> = [
    { id: RECOMMENDATION_YES, labelKey: 'pages.parentFeedback.main.yesLabel', bodyKey: 'pages.parentFeedback.main.yesBody' },
    { id: RECOMMENDATION_OTHERS, labelKey: 'pages.parentFeedback.main.othersLabel', bodyKey: 'pages.parentFeedback.main.othersBody' },
    { id: RECOMMENDATION_NONE, labelKey: 'pages.parentFeedback.main.noneLabel', bodyKey: 'pages.parentFeedback.main.noneBody' },
  ];

  const thumbsItems: Array<{
    key: ThumbsKey;
    question: string;
    issues: [string, string];
  }> = [
    {
      key: 'respectsYourTime',
      question: t('pages.parentFeedback.main.respectsYourTime'),
      issues: [
        t('pages.parentFeedback.main.issuesRespects1'),
        t('pages.parentFeedback.main.issuesRespects2'),
      ],
    },
    {
      key: 'accurateJobDescription',
      question: t('pages.parentFeedback.main.accurateJobDescription'),
      issues: [
        t('pages.parentFeedback.main.issuesAccurate1'),
        t('pages.parentFeedback.main.issuesAccurate2'),
      ],
    },
    {
      key: 'paysBasedOnBooking',
      question: t('pages.parentFeedback.main.paysBasedOnBooking'),
      issues: [
        t('pages.parentFeedback.main.issuesPays1'),
        t('pages.parentFeedback.main.issuesPays2'),
      ],
    },
  ];

  const buttonLabel = isPositive
    ? t('pages.parentFeedback.main.submit')
    : t('pages.parentFeedback.main.next');

  return (
    <BackgroundLayout id='parent-review'>
      <h1 className='text-2xl font-semibold mb-2'>
        {t('pages.parentFeedback.main.heading', { name })}
      </h1>
      <h2 className='text-base text-neutral-600 mb-6'>
        {t('pages.parentFeedback.main.subheading')}
      </h2>

      <div className='space-y-4 mb-6'>
        {recommendationOptions.map((opt) => {
          const selected = review.recommendation?.id === opt.id;
          return (
            <label
              key={opt.id}
              htmlFor={`recommend-${opt.id}`}
              className={`block us-card rounded-[20px] cursor-pointer ${
                selected
                  ? 'border-sapphire bg-sapphire-50'
                  : 'border-neutral-200 bg-white hover:bg-neutral-50'
              }`}
            >
              <input
                id={`recommend-${opt.id}`}
                type='radio'
                name='recommend'
                value={opt.id}
                checked={selected}
                onChange={(): void => setRecommendation(opt.id)}
                className='sr-only'
              />
              <span className='block font-bold'>{t(opt.labelKey)}</span>
              <p className='mb-0 font-light text-sm'>{t(opt.bodyKey, { name })}</p>
            </label>
          );
        })}
      </div>

      <h2 className='text-base font-semibold mt-8 mb-2'>
        {t('pages.parentFeedback.main.thumbsHeading', { name })}
      </h2>
      <div className='flex justify-end gap-6 mb-3 text-sm'>
        <span>{t('pages.parentFeedback.main.yesColLabel')}</span>
        <span>{t('pages.parentFeedback.main.noColLabel')}</span>
      </div>

      <div className='space-y-5 mb-6'>
        {thumbsItems.map(({ key, question, issues }) => {
          const value = review[key];
          return (
            <div key={key}>
              <div className='flex items-center'>
                <span className='flex-1'>{question}</span>
                <div className='flex gap-4'>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={t('pages.parentFeedback.main.yesColLabel')}
                    onClick={(): void => flow.setReview({ [key]: true })}
                    className='text-2xl'
                  >
                    <FontAwesomeIcon
                      icon={value === true ? faThumbsUp : faThumbsUpOutline}
                      className={value === true ? 'text-sapphire' : 'text-neutral-400'}
                    />
                  </Button>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={t('pages.parentFeedback.main.noColLabel')}
                    onClick={(): void => flow.setReview({ [key]: false })}
                    className='text-2xl'
                  >
                    <FontAwesomeIcon
                      icon={value === false ? faThumbsDown : faThumbsDownOutline}
                      className={value === false ? 'text-sapphire' : 'text-neutral-400'}
                    />
                  </Button>
                </div>
              </div>
              <div
                aria-hidden={value !== false}
                className={`grid transition-[grid-template-rows] duration-300 ${
                  value === false ? 'grid-rows-[1fr] mt-2' : 'grid-rows-[0fr]'
                }`}
              >
                <div className='overflow-hidden'>
                  <div className='bg-sapphire-50 rounded p-3'>
                    <div>{t('pages.parentFeedback.main.issuesMayInclude')}</div>
                    <ul className='list-disc pl-5 mb-0'>
                      {issues.map((issue) => (
                        <li key={issue} className='mt-1'>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className='flex flex-col gap-1 mb-6'>
        <label
          htmlFor='review-visibility'
          className='flex items-start gap-2 text-sm cursor-pointer'
        >
          <input
            id='review-visibility'
            type='checkbox'
            checked={flow.reviewVisibility}
            onChange={(e): void => flow.setVisibility(e.target.checked)}
            className='mt-1'
          />
          <span>{t('pages.parentFeedback.main.visibilityLabel', { name })}</span>
        </label>
        <div className='flex items-center gap-2'>
          <a
            id='info-faq'
            title={t('pages.parentFeedback.main.reviewFaqTitle')}
            href={REVIEW_FAQ_URL}
            target='_blank'
            rel='noreferrer'
            className='flex-shrink-0 w-8 h-8 rounded-full border border-neutral-300 flex items-center justify-center text-sm font-semibold'
          >
            i
          </a>
          <span className='italic text-xs text-neutral-600'>
            {t('pages.parentFeedback.main.visibilitySubtext')}
          </span>
        </div>
      </div>

      <div className='text-center mt-5'>
        <Button
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit || submitting}
        >
          {buttonLabel}
        </Button>
      </div>
    </BackgroundLayout>
  );
});
